import { BackgroundJobTaskType } from '~src/backgroundJob/types/BackgroundJobInfo';
import { Formatters, Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ExtractTasks } from '~src/extractTask/resources/ExtractTasks';
import { RunFlowTasks } from '~src/flow/resources/RunFlowTasks';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';

/**
 * Run Now for the Task Details page (Epic 264 stretch: task-action buttons).
 *
 * Dispatches a single task's "Run Now" to the existing per-task webclient wire methods,
 * keyed on the task-type category string the backend puts in `ITaskDetailsResult.taskType`
 * (from `BackgroundJobTypeSet.getName()`, which equals the {@link BackgroundJobTaskType}
 * enum values).
 *
 * v1 scope: Extract, Bridge, and Flow only.
 *  - Extract / Bridge -> `runExtractTasks` (its `ITask.REFRESH_TASK_TYPES` gate already
 *    includes the RefreshExtractViaBridge / IncrementExtractViaBridge task types).
 *  - Flow -> `runFlowTasks`.
 *  - Subscription has no per-task run-now (only schedule-scoped, which would run sibling
 *    tasks), and Encryption / System / PredictiveModelFlow have none either, so the button
 *    is hidden for them (see {@link canRunNow}).
 *
 * Site-level "Run Now disabled" enforcement is backed by the server
 * (`runExtractTasks` / `runFlowTasks` throw `SITE_RUN_NOW_DISABLED`). Note that shape comes
 * back as a *resolved-with-errors* {@link IResult}, for which the error-notifying HTTP client
 * suppresses its own toast — so the CALLER is responsible for surfacing a failure toast when
 * `result.errors.length > 0` (see {@link TaskDetailsSpaceContainer.onRunNow}). Only genuine
 * transport failures are auto-toasted by the reused resources.
 */
export namespace TaskRunNowService {
  const RUNNABLE_TASK_TYPES: ReadonlySet<string> = new Set<string>([
    BackgroundJobTaskType.Extract,
    BackgroundJobTaskType.Bridge,
    BackgroundJobTaskType.Flow,
  ]);

  // S02-contract `contentType` values (must match TaskMetadataSection.CONTENT_TYPE_BY_NAME keys),
  // used to pick the content-named single-task confirmation copy (Figma).
  const ContentType = {
    Datasource: 'Datasource',
    Workbook: 'Workbook',
    VirtualConnection: 'VirtualConnection',
    Flow: 'Flow',
  } as const;

  /**
   * Builds the confirmation-dialog body. Prefers the content-named single-task copy that names the
   * specific data source / workbook / virtual connection / flow (Figma), matching the content-page
   * "Run Now" dialog exactly rather than the bulk "{N} selected extract refreshes" copy. Falls back
   * to the generic single-count prompt when the content name/type is unavailable (e.g. hard-deleted
   * content, or a contentType the S02 contract doesn't cover) so the dialog is never blank.
   */
  function buildRunNowPrompt(
    taskType: string,
    contentName: string | null | undefined,
    contentType: string | null | undefined
  ): string {
    if (contentName) {
      switch (contentType) {
        case ContentType.Datasource:
          return Messages.extractTaskForDataSourceRunPrompt({ DATASOURCE_NAME: contentName });
        case ContentType.Workbook:
          return Messages.extractTaskForWorkbookRunPrompt({ WORKBOOK_NAME: contentName });
        case ContentType.VirtualConnection:
          return Messages.extractTaskForVirtualConnectionRunPrompt({ VIRTUAL_CONNECTION_NAME: contentName });
        case ContentType.Flow:
          return Messages.runFlowsSinglePrompt({ FLOW_NAME: contentName });
        default:
          break;
      }
    }
    return taskType === BackgroundJobTaskType.Flow
      ? Messages.runFlowTasksRunPrompt({ ITEM_COUNT_LOC: Formatters.formatCount(1) })
      : Messages.extractTasksRunPrompt({ ITEM_COUNT_LOC: Formatters.formatCount(1) });
  }

  /** Whether a "Run Now" button should be offered for the given task-type category. */
  export function canRunNow(taskType: string | null | undefined): boolean {
    return !!taskType && RUNNABLE_TASK_TYPES.has(taskType);
  }

  /**
   * Enqueues a single Run Now for the task. `taskId` is the numeric task primary key (tasks.id)
   * as a string — NOT the task LUID: `runExtractTasks`/`runFlowTasks` resolve tasks via
   * `SchedulingService.findTaskById(int)`, and the extract path silently swallows a
   * `NotFoundException`, so passing a LUID here is a no-op. Resolves with the combined
   * {@link IResult} (its `errors` array is empty on a clean enqueue). Callers must gate on
   * {@link canRunNow} first; an unsupported type rejects so a mis-wire is loud rather than silent.
   */
  export function runTaskNow(taskType: string, taskId: string): Promise<IResult> {
    switch (taskType) {
      case BackgroundJobTaskType.Extract:
      case BackgroundJobTaskType.Bridge:
        return ExtractTasks.run([taskId]);
      case BackgroundJobTaskType.Flow:
        return RunFlowTasks.run([taskId]);
      default:
        return Promise.reject(new Error(`Run Now is not supported for task type "${taskType}"`));
    }
  }

  /**
   * Opens the same "Run Now" confirmation dialog the Tasks-tab menu shows (title
   * {@link Messages.runNow}, confirm button {@link Messages.runNowConfirmButton}) and only
   * dispatches after the admin confirms — §7.6 UX parity, so a single click never silently
   * enqueues a production job (matching `RunExtractTasksAction.execute` / `RunFlowTasksAction.dialog`).
   *
   * The returned promise:
   *  - RESOLVES with the wire {@link IResult} when the run completes (its `errors` array drives the
   *    caller's success/failure toast), and
   *  - REJECTS when the admin cancels/dismisses the dialog OR the action itself fails. Those two are
   *    indistinguishable here: `ReactActionDialog` dismisses with `'cancel'` on action failure too, so
   *    the caller must NOT treat a rejection as a failure toast (a false error on a plain cancel is
   *    worse than silence); genuine transport failures are already toasted by the error-notifying HTTP
   *    client behind `ExtractTasks`/`RunFlowTasks`.
   *
   * `contentName`/`contentType` come from the Task Details header (S02 contract) and select the
   * content-named single-task copy (Figma); they're optional so an older/degraded payload safely
   * falls back to the generic single-count prompt (see {@link buildRunNowPrompt}).
   *
   * Callers must gate on {@link canRunNow} (and the site-setting) first.
   */
  export function confirmAndRunTaskNow(
    taskType: string,
    taskId: string,
    contentName?: string | null,
    contentType?: string | null
  ): Promise<IResult> {
    return openReactConfirmationDialog<void, IResult>({
      title: Messages.runNow(),
      actionName: Messages.runNowConfirmButton(),
      message: buildRunNowPrompt(taskType, contentName, contentType),
      action: () => runTaskNow(taskType, taskId),
      paramsForAction: undefined,
      dialogWidth: TabDialogWidth.width375px,
    }).result;
  }
}
